import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import {
  IssuedClient,
  ServiceClient,
  serviceClientAPI,
} from '@/services/apis/serviceClientAPI';

/**
 * 로그인 연동(OIDC 클라이언트) 발급·관리 (#134)
 *
 * 바깥 서비스가 콘솔 계정으로 사용자를 로그인시키게 한다.
 * `client_secret` 은 발급·재발급 때 **한 번만** 보인다 — 서버는 해시만 갖고 있다.
 */

interface ServiceClientDialogProps {
  open: boolean;
  serviceSlug: string | null;
  serviceName?: string;
  onClose: () => void;
}

/** 콜백 주소 한 줄씩 입력받은 것을 목록으로 */
const parseUris = (text: string): string[] =>
  text.split('\n').map((line) => line.trim()).filter(Boolean);

const ServiceClientDialog: React.FC<ServiceClientDialogProps> = ({
  open,
  serviceSlug,
  serviceName,
  onClose,
}) => {
  const [client, setClient] = useState<ServiceClient | null>(null);
  const [issued, setIssued] = useState<IssuedClient | null>(null);
  const [uriText, setUriText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!serviceSlug) return;
    setLoading(true);
    setError('');
    try {
      const found = await serviceClientAPI.get(serviceSlug);
      setClient(found);
      setUriText(found.redirect_uris.join('\n'));
    } catch {
      // 발급 전이면 404 다 — 오류가 아니라 "아직 없음" 이다
      setClient(null);
    } finally {
      setLoading(false);
    }
  }, [serviceSlug]);

  useEffect(() => {
    if (open) {
      setIssued(null);
      setError('');
      setCopied(false);
      load();
    }
  }, [open, load]);

  if (!open || !serviceSlug) return null;

  const run = async (action: () => Promise<void>) => {
    setLoading(true);
    setError('');
    try {
      await action();
    } catch (e: any) {
      setError(e?.response?.data?.detail || '처리하지 못했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleIssue = () =>
    run(async () => {
      const result = await serviceClientAPI.issue(serviceSlug, parseUris(uriText));
      setIssued(result);
      setClient({
        service_slug: result.service_slug,
        client_id: result.client_id,
        redirect_uris: result.redirect_uris,
        is_active: true,
      });
    });

  const handleReissue = () =>
    run(async () => {
      if (!window.confirm('옛 secret 이 즉시 무효가 됩니다. 그 값을 쓰는 서비스는 로그인이 끊깁니다. 계속할까요?')) {
        return;
      }
      setIssued(await serviceClientAPI.reissue(serviceSlug));
    });

  const handleUpdateUris = () =>
    run(async () => {
      const updated = await serviceClientAPI.updateRedirectUris(serviceSlug, parseUris(uriText));
      setClient(updated);
    });

  const handleRevoke = () =>
    run(async () => {
      if (!window.confirm('폐기하면 이 서비스로는 로그인할 수 없습니다. 계속할까요?')) return;
      await serviceClientAPI.revoke(serviceSlug);
      setClient(null);
      setIssued(null);
    });

  const copySecret = async () => {
    if (!issued) return;
    await navigator.clipboard.writeText(issued.client_secret);
    setCopied(true);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-1">로그인 연동</h3>
        <p className="text-sm text-gray-500 mb-4">
          {serviceName || serviceSlug} — 이 서비스가 콘솔 계정으로 사용자를 로그인시킵니다.
          서비스는 자기 회원 테이블을 만들지 않습니다.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        {/* 방금 발급한 secret — 이 화면을 닫으면 다시 볼 수 없다 */}
        {issued && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded">
            <p className="text-sm font-semibold text-amber-900 mb-2">
              client_secret 은 지금 한 번만 보입니다
            </p>
            <p className="text-xs text-amber-800 mb-3">
              서버는 해시만 갖고 있어 다시 보여줄 수 없습니다. 이 창을 닫으면 사라집니다 —
              잃어버리면 재발급해야 하고, 그러면 옛 값은 무효가 됩니다.
            </p>
            <div className="font-mono text-xs bg-white border rounded p-2 break-all mb-2">
              {issued.client_secret}
            </div>
            <Button onClick={copySecret} size="sm" variant="outline">
              {copied ? '복사했습니다' : 'secret 복사'}
            </Button>
          </div>
        )}

        {client ? (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">client_id</label>
              <div className="font-mono text-xs bg-gray-50 border rounded p-2 break-all">
                {client.client_id}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                브라우저에서 도는 앱은 secret 없이 <strong>PKCE</strong> 를 씁니다 —
                그쪽에는 이 값만 알려주면 됩니다.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                콜백 주소 (한 줄에 하나)
              </label>
              <textarea
                value={uriText}
                onChange={(e) => setUriText(e.target.value)}
                rows={3}
                className="w-full border rounded p-2 font-mono text-xs"
                placeholder="https://example.com/auth/callback"
              />
              <p className="text-xs text-gray-500 mt-1">
                <strong>글자 하나까지 같아야</strong> 받습니다. 끝의 <code>/</code> 도 다르면
                다른 주소입니다. 와일드카드는 쓸 수 없고, 운영은 https 만 됩니다
                (로컬 개발은 localhost 만 http 허용).
              </p>
            </div>

            <div className="flex gap-2 pt-3 border-t">
              <Button onClick={handleUpdateUris} disabled={loading} size="sm">
                콜백 주소 저장
              </Button>
              <Button onClick={handleReissue} disabled={loading} size="sm" variant="outline">
                secret 재발급
              </Button>
              <Button onClick={handleRevoke} disabled={loading} size="sm" variant="outline">
                폐기
              </Button>
              <Button onClick={onClose} size="sm" variant="outline" className="ml-auto">
                닫기
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                콜백 주소 (한 줄에 하나)
              </label>
              <textarea
                value={uriText}
                onChange={(e) => setUriText(e.target.value)}
                rows={3}
                className="w-full border rounded p-2 font-mono text-xs"
                placeholder={'https://example.com/auth/callback\nhttp://localhost:5203/auth/callback'}
              />
              <p className="text-xs text-gray-500 mt-1">
                로그인이 끝나면 돌아갈 주소입니다. <strong>글자 하나까지 같아야</strong> 받습니다.
              </p>
            </div>

            <div className="flex gap-2 pt-3 border-t">
              <Button onClick={handleIssue} disabled={loading || !parseUris(uriText).length} size="sm">
                발급
              </Button>
              <Button onClick={onClose} size="sm" variant="outline" className="ml-auto">
                닫기
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceClientDialog;
